import {Component, inject} from '@angular/core';
import {
  ACCOUNT_LOGIN_ROUTE, CREATE_ACCOUNT_ROUTE, DEFAULT_ROUTE_CONSTANT,
  DESTINATION_KEY_CONSTANT, FRESH_FROM_SOURCE_CONSTANT, LOGIN_OBJECT_KEY_CONSTANT,
  LOGIN_SUCCESSFUL_ROUTE, PREVIOUS_DESTINATION_KEY_CONSTANT,
  PRODUCT_KEY_CONSTANT, REDIRECTED_FLAG_CONSTANT
} from "../constants/routes.constant";
import {from, map, mergeMap, Subject} from "rxjs";
import {PROGRESS_BAR_SUBJECT} from "../app.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FirebaseAuthService} from "../services/firebase-auth.service";
import {AuthenticationService} from "../services/authentication.service";
import {ActivatedRoute, Router} from "@angular/router";
import {Location} from "@angular/common";
import {SESSION_TOKEN_KEY} from "../constants/local-storage.constant";
import {HttpErrorResponse} from "@angular/common/http";

@Component({
  selector: 'app-sign-in-with-apple',
  templateUrl: './sign-in-with-apple.component.html',
  styleUrl: './sign-in-with-apple.component.scss',
  standalone: false,
})
export class SignInWithAppleComponent {
  public inProgress: boolean = false;
  private readonly loginSuccessfulRoute: string = LOGIN_SUCCESSFUL_ROUTE;
  private readonly loginRoute: string = ACCOUNT_LOGIN_ROUTE;
  private readonly createAccountRoute: string = CREATE_ACCOUNT_ROUTE;
  private productName!: string;
  private destination!: string;
  private loginObject!: string;
  private previousDestination!: string;
  private readonly productNameKey: string = PRODUCT_KEY_CONSTANT;
  private readonly destinationKey: string = DESTINATION_KEY_CONSTANT;
  private readonly loginObjectKey: string = LOGIN_OBJECT_KEY_CONSTANT;
  private readonly previousDestinationKey: string = PREVIOUS_DESTINATION_KEY_CONSTANT;
  public queryParams: { [key: string]: string } = {
    [this.productNameKey]: this.productName,
    [this.destinationKey]: this.destination,
    [this.loginObjectKey]: this.loginObject,
    [this.previousDestinationKey]: this.previousDestination,
  };
  private freshFromSourceKey: string = FRESH_FROM_SOURCE_CONSTANT;
  private freshFromSource!: string;
  public subject: Subject<boolean> = PROGRESS_BAR_SUBJECT;

  private readonly snackBar: MatSnackBar = inject(MatSnackBar);
  private readonly firebaseAuthService: FirebaseAuthService = inject(FirebaseAuthService);
  private readonly authenticationService: AuthenticationService = inject(AuthenticationService);
  private readonly router: Router = inject(Router);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly location: Location = inject(Location);

  ngOnInit() {
    this.productName = this.activatedRoute.snapshot.queryParamMap.get(
      this.productNameKey
    )!;
    this.destination = this.activatedRoute.snapshot.queryParamMap.get(
      this.destinationKey
    )!;
    this.loginObject = this.activatedRoute.snapshot.queryParamMap.get(
      this.loginObjectKey
    )!;
    this.previousDestination = this.activatedRoute.snapshot.queryParamMap.get(
      this.previousDestinationKey
    )!;
    this.freshFromSource = this.activatedRoute.snapshot.queryParamMap.get(
      this.freshFromSourceKey
    )!;

    this.queryParams = {
      [this.productNameKey]: this.productName,
      [this.destinationKey]: this.destination,
      [this.loginObjectKey]: this.loginObject,
      [this.previousDestinationKey]: this.previousDestination,
    };

    if (!this.productName || !this.destination) {
      this.router.navigate(DEFAULT_ROUTE_CONSTANT, {
        queryParams: this.queryParams,
      });
    }

    if (!this.freshFromSource) {
      this.completeSignInWithAppleUsingRedirect();
    } else {
      // reload removing the ffs
      this.removeParamsWithoutNavigation();
      this.signInWithApple();
    }
  }

  public removeParamsWithoutNavigation() {
    const currentPath = this.location.path()
      .replace('&ffs=true', ''); // Get the URL path without the query string

    this.location.replaceState(currentPath); // Replace the current state in history
  }

  public signInWithApple() {
    this.inProgress = true;
    this.subject.next(true);

    localStorage.removeItem(REDIRECTED_FLAG_CONSTANT);

    from(this.firebaseAuthService.signInWithApple()).subscribe({
      next: () => {
        // INFO Nothing here matters because the application is typically closed when the redirect happens
      },
      error: () => {
        this.subject.next(false);
        this.inProgress = false;
        this.snackBar.open('An error occurred!', 'close', {
          verticalPosition: 'top',
          horizontalPosition: 'end',
          panelClass: [
            'bg-danger',
            'text-white',
            'rounded-3e',
            'modified-snackbar',
          ],
        });
      },
    });
  }

  public completeSignInWithAppleUsingRedirect() {
    localStorage.removeItem(REDIRECTED_FLAG_CONSTANT);

    this.subject.next(true);
    this.inProgress = true;

    from(this.firebaseAuthService.getRedirectResult())
      .pipe(
        mergeMap((userCredentials) => this.firebaseAuthService
          .getIdToken()
          .pipe(map((idTokenResult) => ({ idTokenResult, userCredentials })))
        ),
        mergeMap((d) => {
          return this.authenticationService
            .authenticateUser(d.idTokenResult.token)
            .pipe(map((token) => {
              localStorage.setItem(SESSION_TOKEN_KEY, token);

              return { sessionToken: token, ...d }
            }));
        }),
      )
      .subscribe({
        next: () => {
          this.inProgress = false;
          this.goToLoginSuccessfulRoute();
        },
        error: (error: HttpErrorResponse | any) => {
          console.error('Sign-in with Apple Error:', error);

          this.subject.next(false);
          this.inProgress = false;

          const queryParams = { ...this.queryParams, [FRESH_FROM_SOURCE_CONSTANT]: 'true' };
          let errorMessage = 'An error occurred during authentication!';

          if (error?.status === 404) {
            errorMessage = 'User not registered, taking you to register now';

            this.router.navigate(
              ['/', ...this.createAccountRoute.split('/')],
              {
                queryParams,
                queryParamsHandling: 'merge',
              }
            );
          } else {
            this.router.navigate(
              ['/', ...this.loginRoute.split('/')],
              {
                queryParams,
                queryParamsHandling: 'merge',
              }
            );
          }

          this.snackBar.open(errorMessage, 'close', {
            verticalPosition: 'top',
            horizontalPosition: 'end',
            panelClass: [
              'bg-danger',
              'text-white',
              'rounded-3e',
              'modified-snackbar',
            ],
          });
        },
      });
  }

  public goToLoginSuccessfulRoute() {
    this.router.navigate(['/', ...this.loginSuccessfulRoute.split('/')], {
      queryParams: this.queryParams,
      queryParamsHandling: 'preserve',
    });
  }
}
